import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const Privacy: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <>
      <Helmet>
        <title>{t('metadata.privacy.title')}</title>
        <meta name="description" content={t('metadata.privacy.description')} />
        <link rel="canonical" href="https://iptvplayer.stream/privacy" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Privacy Policy</h1>
        
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Introduction</h2>
            <p className="text-gray-600 dark:text-gray-400">
              At IPTVPlayer.stream, we take your privacy seriously. This Privacy Policy explains how we collect,
              use, and protect your personal information when you use our service. This policy should be read in conjunction with our{' '}
              <Link 
                to="/terms" 
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                Terms of Service
              </Link>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Information We Collect</h2>
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                We collect the following types of information:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 space-y-2">
                <li>Email address (for account creation and communication)</li>
                <li>Playlist data (channels and favorites you add to the platform)</li>
                <li>Usage data (how you interact with our service)</li>
                <li>Technical data (browser type, device information)</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">How We Use Your Information</h2>
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                We use your information to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 space-y-2">
                <li>Provide and maintain our service</li>
                <li>Notify you about changes to our service</li>
                <li>Provide customer support</li>
                <li>Monitor the usage of our service</li>
                <li>Detect, prevent and address technical issues</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data Security</h2>
            <p className="text-gray-600 dark:text-gray-400">
              We implement appropriate security measures to protect your personal information.
              Your data is stored securely using industry-standard encryption and security practices.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Contact Us</h2>
            <p className="text-gray-600 dark:text-gray-400">
              If you have any questions about this Privacy Policy, please{' '}
              <Link 
                to="/contact" 
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                contact us
              </Link>
              {' '}or email us at{' '}
              <a 
                href="mailto:support@iptvplayer.stream"
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                support@iptvplayer.stream
              </a>
              . For general help and information, visit our{' '}
              <Link to="/m3u-hls-player-faq" className="text-blue-500 hover:text-blue-600 transition-colors">
                {t('footer.faq', 'FAQ')}
              </Link>.
            </p>
          </section>

          <div className="text-sm text-gray-500 dark:text-gray-400 pt-8 border-t border-gray-200 dark:border-gray-700">
            Last updated: {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>
    </>
  );
}; 